import { useTranslation } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { Loading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { Page } from 'module/ui/components/molecules/page';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faArrowsLeftRight,
  faCircleExclamation,
  faRectangleHistory,
  faSidebar,
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical, faSignal } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { useLocalStorageState } from 'ahooks';
import { SignalType } from 'api/signal/types';
import { ReportedBy } from 'api/timeline/types';
import isEqual from 'lodash/isEqual';
import { useGetFilteredSignalsQuery } from 'queries/all-signals';
import { FormattedMessage } from 'react-intl';
import { EmptyState } from 'components/atoms/empty-state';
import { IconWrapper } from 'components/common/styles';
import { SignalOutputCard } from 'components/organisms/signal-output';
import { AllSignalsTable } from 'components/organisms/tables/all-signals-table';
import { AccountHeader } from 'components/pages/accounts/detail/common/components';
import { ViewSwitcher } from 'components/pages/accounts/view-switcher';
import {
  useAllSignalsContext,
  AllSignalsProvider,
} from './all-signals-context';
import { useSavedViews } from './use-saved-views';
import {
  AllSignalsSidebar,
  SignalsListWrapper,
  SignalsContainer,
  SignalsContainerListView,
  EmptyStateIcon,
  PrimaryActionsContainer,
  SecondaryActionsContainer,
  StyledSidebarIcon,
  StyledSignalIcon,
  StyledRectangleHistoryIcon,
  FullWidthMenuItem,
} from '.';

interface AllSignalsCommonProps {
  isListView?: boolean;
}

const AllSignalsCommonContent = ({
  isListView = false,
}: AllSignalsCommonProps) => {
  const searchPlaceholder = useTranslation('search');
  const [isFullWidthActive, setIsFullWidthActive] =
    useLocalStorageState<boolean>('all_signals_full_width');

  const {
    filter,
    signalFilterParams,
    value,
    savedViewsSelected,
    sharedViewsSelected,
    selectedSignals,
    isSidebarOpen,
    onChange,
    clear,
    onFilterChange,
    onDone,
    onCancel,
    setIsSidebarOpen,
    setSelectedSignals,
    groupedFields,
  } = useAllSignalsContext();

  const savedViewsResult = useSavedViews({
    setSelectedSignalUnderMyAccounts: () => {},
    setSavedViewsSelected: () => {},
    setSharedViewsSelected: () => {},
    replaceFilter: onFilterChange,
    savedViewsSelected,
    sharedViewsSelected,
  });

  const { selectedSavedView, handleSaveView, handleUpdateView } =
    savedViewsResult;

  const filtersChanged =
    !isListView && !!selectedSavedView
      ? !isEqual(filter, selectedSavedView.filter) ||
        signalFilterParams.signalType !== selectedSavedView.signalType ||
        signalFilterParams.myAccountSignalType !==
          selectedSavedView.myAccountSignalType ||
        !isEqual(
          signalFilterParams.selectedSignals,
          selectedSavedView.selectedSignals,
        )
      : false;

  const SignalsContent = () => {
    const {
      data: filteredSignals,
      isLoading: isFilteredSignalsLoading,
      isFetching: isFilteredSignalsFetching,
    } = useGetFilteredSignalsQuery({
      filter,
      signalConfigurationId: 0,
      signalType: signalFilterParams.signalType as SignalType,
      searchValue: value,
      startRow: 0,
      endRow: 100,
      myAccountSignalType: signalFilterParams.myAccountSignalType,
    });

    if (isFilteredSignalsLoading || isFilteredSignalsFetching) {
      return <Loading />;
    }

    if (!filteredSignals || filteredSignals?.length === 0) {
      return (
        <EmptyState
          title="No signals have been detected"
          image={<EmptyStateIcon icon={faCircleExclamation} size={40} />}
        />
      );
    }

    if (isListView) {
      return <AllSignalsTable signals={filteredSignals} />;
    }

    return (
      <>
        {filteredSignals?.map((signal, index) => {
          const reason = signal?.fields?.find(
            (field) =>
              Object.keys(signal?.metaData)[0] === String(field?.fieldId),
          );

          return (
            <SignalOutputCard
              key={signal?.signalOccurrenceId || index}
              sourceId={signal?.sourceId}
              sourceType={signal?.sourceType}
              people={signal?.reportedBy ?? []}
              reason={reason}
              title={signal?.title}
              description={signal?.summary}
              tasks={[]}
              insightfulContent={''}
              type={signal?.signalName}
              citations={signal?.citationMapping}
              signalType={signal?.signalType}
              className="timeline-details-signal"
              showCompanyDetails={true}
              companyName={
                (signal?.reportedBy as unknown as ReportedBy)?.company
                  ?.companyName || signal?.reportedBy?.[0]?.company?.companyName
              }
              companyLogo={
                (signal?.reportedBy as unknown as ReportedBy)?.company
                  ?.companyLogoUrl ||
                signal?.reportedBy?.[0]?.company?.companyLogoUrl
              }
              createdAt={signal?.createdAt}
            />
          );
        })}
      </>
    );
  };

  const renderPrimaryActions = () => (
    <PrimaryActionsContainer>
      <Button
        variant="ghost"
        leftIcon={<StyledSidebarIcon icon={faSidebar} />}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      {/* Samyuktha: removed this  */}
      {/* {!isListView && (
        <SelectedSignalType>
          {signalFilterParams.myAccountSignalType}
        </SelectedSignalType>
      )} */}
      <AccountHeader
        groupedFields={groupedFields}
        filter={filter}
        onChange={onFilterChange}
        onDone={onDone}
        onCancel={onCancel}
        setSelectedSignals={setSelectedSignals}
        selectedSignals={selectedSignals}
        onSaveView={
          !isListView
            ? (data: { name: string; filter: any; viewAvailability: string }) =>
                handleSaveView({
                  viewName: data.name,
                  filter: data.filter,
                  self: data.viewAvailability === 'ONLY_ME',
                })
            : undefined
        }
        onUpdateView={
          !isListView
            ? (data: { name: string; filter: any; viewAvailability: string }) =>
                handleUpdateView({
                  viewName: data.name,
                  filter: data.filter,
                  self: data.viewAvailability === 'ONLY_ME',
                })
            : undefined
        }
        isViewSelected={!isListView ? !!savedViewsSelected : undefined}
        filtersChanged={!isListView ? filtersChanged : undefined}
        currentViewName={selectedSavedView?.viewName}
      />
    </PrimaryActionsContainer>
  );

  const renderSecondaryActions = () => {
    return (
      <SecondaryActionsContainer>
        {isListView ? (
          <SearchInputExpanded
            placeholder={searchPlaceholder}
            value={value}
            onChange={onChange}
            onClear={clear}
          />
        ) : null}
        <ViewSwitcher
          primaryPageUrl={'/all-signals'}
          secondaryPageUrl={'/all-signals/list'}
          primaryViewName="All Signals"
          primaryIcon={<StyledRectangleHistoryIcon icon={faRectangleHistory} />}
        />
        {isListView ? null : (
          <MoreActionsDropdown
            menu={{
              onClick: (e) => {
                e.domEvent.stopPropagation();
                e.domEvent.preventDefault();
                if (e.key === 'full-width') {
                  if (isFullWidthActive) {
                    setIsFullWidthActive(false);
                  } else {
                    setIsFullWidthActive(true);
                  }
                }
              },
              items: [
                {
                  key: 'full-width',
                  label: (
                    <FullWidthMenuItem>
                      <FormattedMessage id="full_width" />
                      <Switch size="small" checked={isFullWidthActive} />
                    </FullWidthMenuItem>
                  ),
                  icon: <FontAwesomeIcon icon={faArrowsLeftRight} size={12} />,
                },
              ],
            }}
            triggerElement={
              <Button
                variant="secondary"
                leftIcon={
                  <FontAwesomeIcon icon={faEllipsisVertical} size={12} />
                }
              />
            }
          />
        )}
      </SecondaryActionsContainer>
    );
  };

  return (
    <Argon
      documentTitle="All Signals"
      title={
        <IconWrapper>
          <StyledSignalIcon icon={faSignal} />
          <Page.Header.Title>All Signals</Page.Header.Title>
        </IconWrapper>
      }
      sider={
        isSidebarOpen ? (
          <AllSignalsSidebar searchPlaceholder={searchPlaceholder} />
        ) : null
      }
      content={
        <SignalsListWrapper>
          {isListView ? (
            <SignalsContainerListView>
              <SignalsContent />
            </SignalsContainerListView>
          ) : (
            <SignalsContainer $isFullWidth={isFullWidthActive}>
              <SignalsContent />
            </SignalsContainer>
          )}
        </SignalsListWrapper>
      }
      actions={{
        primary: renderPrimaryActions(),
        secondary: renderSecondaryActions(),
      }}
    />
  );
};

export const AllSignalsCommon = (props: AllSignalsCommonProps) => {
  return (
    <AllSignalsProvider>
      <AllSignalsCommonContent isListView={props.isListView} />
    </AllSignalsProvider>
  );
};
